import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Zero Trust identity',
    metaDescription:
      'Continuous verification, zero standing privilege, least-privilege access, and a defensible audit trail: the identity foundation for Zero Trust.',
    heroEyebrow: 'Use cases · Zero Trust',
    heroTitle: ['Zero Trust starts', 'with identity.'],
    heroBody:
      'Continuous verification, zero standing privilege, least-privilege access, and a tamper-evident audit trail. Monofor turns Zero Trust from a slogan into the way your identity layer actually runs.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'Continuous verification',
      'Zero standing privilege',
      'Tamper-evident audit',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'The identity discipline behind a real Zero Trust posture.',
    pillarsBody:
      'Most Zero Trust programs stall on the identity layer. Monofor gives you the four primitives (continuous verification, zero standing access, least privilege, and proof) without stitching five tools together.',
    pillars: [
      {
        title: 'Verify the user, every time',
        description:
          'Continuous, risk-aware authentication. Treat every sign-in as a fresh decision based on user, device, and behavior signals.',
        bullets: [
          'Adaptive MFA tied to live risk signals',
          'Device posture as a policy input',
          'Behavioral anomaly detection',
          'Continuous session evaluation, not one-shot',
        ],
      },
      {
        title: 'Eliminate standing privilege',
        description:
          'Default state: zero. Privileges only exist for the duration of an approved task, and the gateway records the entire session.',
        bullets: [
          'No permanent admin groups',
          'Time-bound, approval-driven elevation',
          'Brokered sessions, never raw credentials',
          'Auto-revoke regardless of behavior',
        ],
      },
      {
        title: 'Least-privilege as the default shape',
        description:
          'Access is requested, justified, granted narrowly, and reviewed regularly. Wide standing access is the exception, not the norm.',
        bullets: [
          'Per-asset, per-command, per-table scoping',
          'Self-service access requests with approval',
          'Scheduled access reviews by risk tier',
          'Auto-revoke unattested access',
        ],
      },
      {
        title: 'Observable, exportable, defensible',
        description:
          'Every identity decision is logged, searchable, and tied to evidence. Zero Trust is only credible if you can prove it on demand.',
        bullets: [
          'Immutable, tamper-evident audit log',
          'Linked to session recordings',
          'Framework-aligned reports (SOC 2, ISO 27001)',
          'API access for SIEM and SOAR',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Verify. Eliminate. Prove.',
    steps: [
      {
        title: 'Verify continuously',
        description:
          'Replace one-shot sign-on with continuous, risk-aware authentication on every request and at session events.',
      },
      {
        title: 'Remove standing access',
        description:
          'Move admin groups under Monopam control. Default to zero; elevate on approval, time-bound and recorded.',
      },
      {
        title: 'Prove the posture',
        description:
          'Wire audit exports into your GRC, SIEM, and assessor workflows. Posture becomes a continuous artifact, not a one-time claim.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Just-in-time access',
        description: 'The PAM capability that eliminates standing privilege.',
        href: '/pam/jit-access',
      },
      {
        label: 'Multi-Factor Authentication',
        description: 'Phishing-resistant verification with FIDO2 / WebAuthn.',
        href: '/sign/mfa',
      },
      {
        label: 'Audit & compliance',
        description: 'Make Zero Trust posture defensible to auditors.',
        href: '/solutions/audit-compliance',
      },
    ],
  },
  tr: {
    metaTitle: 'Sıfır Güven kimliği',
    metaDescription:
      'Sürekli doğrulama, sıfır kalıcı ayrıcalık, en az ayrıcalıklı erişim ve savunulabilir bir denetim izi: Sıfır Güven için kimlik temeli.',
    heroEyebrow: 'Kullanım senaryoları · Sıfır Güven',
    heroTitle: ['Sıfır Güven', 'kimlikle başlar.'],
    heroBody:
      'Sürekli doğrulama, sıfır kalıcı ayrıcalık, en az ayrıcalıklı erişim ve kurcalamaya karşı korumalı bir denetim izi. Monofor, Sıfır Güven’i bir slogandan kimlik katmanınızın gerçek çalışma biçimine dönüştürür.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Sürekli doğrulama',
      'Sıfır kalıcı ayrıcalık',
      'Kurcalamaya karşı korumalı denetim',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle:
      'Gerçek bir Sıfır Güven duruşunun arkasındaki kimlik disiplini.',
    pillarsBody:
      'Çoğu Sıfır Güven programı kimlik katmanında tıkanır. Monofor size dört temel yapı taşını (sürekli doğrulama, sıfır kalıcı erişim, en az ayrıcalık ve kanıt) beş ayrı aracı birbirine dikmeden sunar.',
    pillars: [
      {
        title: 'Kullanıcıyı her seferinde doğrulayın',
        description:
          'Sürekli, risk farkındalıklı kimlik doğrulama. Her oturum açmayı kullanıcı, cihaz ve davranış sinyallerine dayalı yeni bir karar olarak ele alın.',
        bullets: [
          'Canlı risk sinyallerine bağlı uyarlanabilir MFA',
          'Politika girdisi olarak cihaz durumu',
          'Davranışsal anomali tespiti',
          'Tek seferlik değil, sürekli oturum değerlendirmesi',
        ],
      },
      {
        title: 'Kalıcı ayrıcalığı ortadan kaldırın',
        description:
          'Varsayılan durum: sıfır. Ayrıcalıklar yalnızca onaylanmış bir görev süresince var olur; ağ geçidi oturumun tamamını kaydeder.',
        bullets: [
          'Kalıcı yönetici grubu yok',
          'Süreli, onaya dayalı yetki yükseltme',
          'Ham kimlik bilgisi değil, aracılı oturumlar',
          'Her koşulda otomatik geri alma',
        ],
      },
      {
        title: 'Varsayılan olarak en az ayrıcalık',
        description:
          'Erişim talep edilir, gerekçelendirilir, dar kapsamlı verilir ve düzenli olarak gözden geçirilir. Geniş kalıcı erişim norm değil, istisnadır.',
        bullets: [
          'Varlık, komut ve tablo bazında kapsam belirleme',
          'Onaylı self servis erişim talepleri',
          'Risk kademesine göre planlı erişim gözden geçirmeleri',
          'Doğrulanmayan erişimin otomatik geri alınması',
        ],
      },
      {
        title: 'Gözlemlenebilir, dışa aktarılabilir, savunulabilir',
        description:
          'Her kimlik kararı loglanır, aranabilir ve kanıta bağlıdır. Sıfır Güven, ancak talep üzerine kanıtlayabildiğinizde inandırıcıdır.',
        bullets: [
          'Değiştirilemez, kurcalamaya karşı korumalı denetim logu',
          'Oturum kayıtlarıyla ilişkilendirilmiş kanıt',
          'Çerçeveyle hizalı raporlar (SOC 2, ISO 27001)',
          'SIEM ve SOAR için API erişimi',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Doğrulayın. Kaldırın. Kanıtlayın.',
    steps: [
      {
        title: 'Sürekli doğrulayın',
        description:
          'Tek seferlik oturum açmayı, her istekte ve oturum olaylarında çalışan sürekli, risk farkındalıklı kimlik doğrulamayla değiştirin.',
      },
      {
        title: 'Kalıcı erişimi kaldırın',
        description:
          'Yönetici gruplarını Monopam kontrolüne alın. Varsayılan sıfır olsun; onayla, süreli ve kayıt altında yükseltin.',
      },
      {
        title: 'Duruşu kanıtlayın',
        description:
          'Denetim dışa aktarımlarını GRC, SIEM ve denetçi iş akışlarınıza bağlayın. Duruş, tek seferlik bir iddia değil, sürekli bir kanıt haline gelir.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Just-in-time erişim',
        description: 'Kalıcı ayrıcalığı ortadan kaldıran PAM yeteneği.',
        href: '/pam/jit-access',
      },
      {
        label: 'Çok Faktörlü Kimlik Doğrulama',
        description: 'FIDO2 / WebAuthn ile kimlik avına dirençli doğrulama.',
        href: '/sign/mfa',
      },
      {
        label: 'Denetim ve uyum',
        description: 'Sıfır Güven duruşunu denetçilere karşı savunulabilir kılın.',
        href: '/solutions/audit-compliance',
      },
    ],
  },
}

type Props = { locale: string }

export default function ZeroTrustPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/zero-trust"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
